import {Component, computed, inject, signal, WritableSignal} from '@angular/core';
import {MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from '@angular/material/dialog';
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {teamMember} from "../../shared/header/team-management/team-management.component";
import {Session} from "../../../services/session.service";
import {first} from "rxjs/operators";
import {ApiCallService} from "../../../services/api-call.service";
import {UserAvatarComponent} from "../../shared/user-avatar/user-avatar.component";
import {forkJoin, from, switchMap} from "rxjs";
import {CurrencyPipe, DatePipe} from "@angular/common";
import {WeaveButtonComponent} from "../../../shared/forms/weave-button/weave-button.component";
import { MatDialog } from '@angular/material/dialog';
import { CancelAccountModalComponent } from '../cancel-account-modal/cancel-account-modal.component';
import {StripeElements, StripePaymentElementOptions} from "@stripe/stripe-js";
import {StripeElementsDirective, StripePaymentElementComponent, StripeService} from "ngx-stripe";
import {SubscriptionService} from "../../../services/subscription.service";
import {AccountStatus} from "../../../interfaces/account-status";

@Component({
  selector: 'app-account-management-modal',
  templateUrl: './account-management-modal.component.html',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatIcon,
    MatIconButton,
    UserAvatarComponent,
    CurrencyPipe,
    WeaveButtonComponent,
    StripeElementsDirective,
    StripePaymentElementComponent,
    DatePipe,
  ],
  styleUrls: ['./account-management-modal.component.scss']
})
export class AccountManagementModalComponent{
  private readonly dialogRef: MatDialogRef<AccountManagementModalComponent> = inject(MatDialogRef<AccountManagementModalComponent>);
  private readonly session: Session = inject(Session);
  private readonly apiService = inject(ApiCallService);
  private readonly dialog = inject(MatDialog);
  private readonly subscriptionService = inject(SubscriptionService);
  protected readonly stripe = inject(StripeService);

  teamMembers: WritableSignal<teamMember[]> = signal([]);
  subscriptionInfo: WritableSignal<subscription | null> = signal(null);
  errorMessage: WritableSignal<string> = signal('');
  customerSessionClientSecret: WritableSignal<string> = signal('');
  setupIntentClientSecret: WritableSignal<string> = signal('');
  savingPaymentMethod = signal(false);
  paymentFormChanged = signal(false);
  protected readonly accountStatus: AccountStatus = this.session.getAccountStatus();
  private stripeElementsInstance?: StripeElements;

  private initialLoadComplete = false;

  stripeElementsOptions = computed<any>(() => {
    this.initialLoadComplete = false; // Reset when secret changes
    const secret = this.customerSessionClientSecret();
    const setupSecret = this.setupIntentClientSecret();
    console.log('Computing stripeElementsOptions', { secret, setupSecret });
    if (!secret || !setupSecret) return null;
    return {
      locale: 'en',
      clientSecret: setupSecret,
      customerSessionClientSecret: secret,
      appearance: {
        theme: 'stripe',
        labels: 'above',
        variables: {
          colorPrimary: '#292926',
          fontFamily: '"Inter", sans-serif',
          borderRadius: '8px',
        },
      },
    }
  });

  paymentElementOptions = computed<StripePaymentElementOptions>(() => this.subscriptionService.getPaymentElementOptions('accordion'));

  constructor() {
    this.getTeamAndAccountStatus();
    this.getStripeCustomerSession();
  }

  cancelSubscription() {
    this.dialogRef.close();
    setTimeout(() => {
      this.dialog.open(CancelAccountModalComponent, {
        width: '400px',
        disableClose: true
      });
    }, 250);
  }

  onElementsCreated(elements: StripeElements) {
    console.log('onElementsCreated called', elements);
    this.stripeElementsInstance = elements;
  }

  onPaymentElementChange(event: any) {
    console.log('onPaymentElementChange', event);

    // After the initial event, any subsequent change event likely indicates user interaction.
    if (this.initialLoadComplete) {
      this.paymentFormChanged.set(true);
    }

    if (!this.initialLoadComplete) {
      this.initialLoadComplete = true;
    }
  }

  savePaymentMethod() {
    console.log('savePaymentMethod called');
    console.log('savingPaymentMethod:', this.savingPaymentMethod());
    console.log('stripeElementsInstance:', !!this.stripeElementsInstance);

    if (this.savingPaymentMethod() || !this.stripeElementsInstance) {
      console.warn('savePaymentMethod early return:', {
        saving: this.savingPaymentMethod(),
        noInstance: !this.stripeElementsInstance
      });
      return;
    }
    console.log('Saving payment method...');

    this.savingPaymentMethod.set(true);
    this.errorMessage.set('');

    console.log(this.setupIntentClientSecret())
    console.log(this.stripeElementsInstance)

    // 1. First, call submit() on elements
    from(this.stripeElementsInstance.submit()).pipe(
      // 2. If submit is successful, then call confirmSetup
      switchMap((result) => {
        if (result.error) {
          // If there's an error during submission (e.g. validation), throw it to be handled in subscribe
          throw result.error;
        }

        return this.stripe.confirmSetup({
          elements: this.stripeElementsInstance!,
          clientSecret: this.setupIntentClientSecret(),
          redirect: 'if_required',
          confirmParams: {
            return_url: window.location.href,
          },
        });
      })
    ).subscribe({
      next: (result) => {
        this.savingPaymentMethod.set(false);
        if (result.error) {
          this.errorMessage.set(result.error.message || 'An error occurred.');
        } else {
          // Success logic
          this.paymentFormChanged.set(false);
          this.initialLoadComplete = false;
          this.setupIntentClientSecret.set("");
          this.customerSessionClientSecret.set("");
          this.getStripeCustomerSession();
        }
      },
      error: (err) => {
        this.savingPaymentMethod.set(false);
        this.errorMessage.set(err.message || 'An unexpected error occurred.');
      }
    });
  }

  getStripeCustomerSession() {
    this.apiService.getData('/getStripeCustomerSession/', null).pipe(first()).subscribe((response) => {
      if (response?.success && response?.data) {
        this.customerSessionClientSecret.set(response.data[0].dataSet.customerSessionClientSecret);
        this.setupIntentClientSecret.set(response.data[0].dataSet.setupIntentClientSecret);
      }
    });
  }

  getTeamAndAccountStatus() {
    forkJoin({
      teamResponse: this.apiService.getData('/getHeaderTeam/', null).pipe(first()),
      accountStatusResponse: this.apiService.getData('/getAccountStatus/', null).pipe(first())
    }).subscribe(({ teamResponse, accountStatusResponse }) => {
      // Handle team response
      if (teamResponse?.success && teamResponse?.data && teamResponse?.data[0].dataSet.teamMembers) {
        this.getTeamMembers(teamResponse.data[0].dataSet.teamMembers);
      } else {
        if(teamResponse && teamResponse.dataError && Array.isArray(teamResponse.dataError) && teamResponse.dataError.length > 0) {
          this.errorMessage.set("There was a problem with loading the team. The server responded with: " + teamResponse.dataError[0].message);
          return;
        }
        else {
          this.errorMessage.set("There was a problem with loading the team. The server did not return any error messages. Please contact support.");
        }
      }

      // Handle account status response
      if (accountStatusResponse?.success && accountStatusResponse?.data) {
        this.subscriptionInfo.set({
          userPrice: accountStatusResponse.data[0].dataSet.subscriptionUserPrice,
          totalPrice: accountStatusResponse.data[0].dataSet.subscriptionPrice,
          basePrice: accountStatusResponse.data[0].dataSet.subscriptionPrice - accountStatusResponse.data[0].dataSet.subscriptionUserTotalPrice,
        });

      } else {
        if(accountStatusResponse && accountStatusResponse.dataError && Array.isArray(accountStatusResponse.dataError) && accountStatusResponse.dataError.length > 0) {
          this.errorMessage.set("There was a problem with loading the account status. The server responded with: " + accountStatusResponse.dataError[0].message);
          return;
        }
        else {
          this.errorMessage.set("There was a problem with loading the account status. The server did not return any error messages. Please contact support.");
        }
      }
    });
  }

  getTeamMembers(teamMembers ) {
    let members: teamMember[] = [];
    teamMembers
      .filter(member => member.requestKey !== this.session.getUser().userRequestKey)
      .forEach((member) => {
        const names = member.fullName ? member.fullName.split( ' ' ) : [ '', '' ];
        const firstName = names[ 0 ] || '';
        const lastName = names.slice( 1 ).join( ' ' ) || '';
        const teamMember = {
          name: member.fullName,
          firstName: firstName,
          lastName: lastName,
          requestKey: member.requestKey,
          avatar: member.avatar,
          userRole: member.userRole,
          pending: member.pending,
          email: member.email ? member.email : member.fullName,
          inviteDate: member.dateInvited ? new Date(member.dateInvited) : null,
        }
        members.push(teamMember);
      });

    this.teamMembers.set(members);
  }

  close(): void {
    this.dialogRef.close();
  }
}

interface subscription{
  userPrice: number;
  totalPrice: number;
  basePrice: number;
}
